import { create } from "zustand";
import type { LiveTranscriptLine } from "../types/liveTranscript";
import {
  isStaleTranscriptSession,
  upsertLiveTranscriptLine,
} from "../types/liveTranscript";

export interface LiveKitConnectionData {
  token: string;
  url: string;
  room_name?: string;
}

interface HeyGenState {
  liveKitData: LiveKitConnectionData | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;

  isRecording: boolean;
  audioStream: MediaStream | null;

  sessionActive: boolean;
  windowTitle: string | null;
  liveTranscript: LiveTranscriptLine[];
  liveTranscriptSessionId: string | null;

  setLiveKitData: (data: LiveKitConnectionData | null) => void;
  setIsConnected: (connected: boolean) => void;
  setIsConnecting: (connecting: boolean) => void;
  setError: (error: string | null) => void;
  setIsRecording: (recording: boolean) => void;
  setAudioStream: (stream: MediaStream | null) => void;
  setSessionActive: (active: boolean) => void;
  setWindowTitle: (title: string | null) => void;
  setLiveTranscriptSessionId: (sessionId: string | null) => void;
  applyLiveTranscriptLine: (line: LiveTranscriptLine) => void;
  clearLiveTranscript: () => void;
  reset: () => void;
}

export const useHeyGenStore = create<HeyGenState>((set) => ({
  liveKitData: null,
  isConnected: false,
  isConnecting: false,
  error: null,
  isRecording: false,
  audioStream: null,
  sessionActive: false,
  windowTitle: null,
  liveTranscript: [],
  liveTranscriptSessionId: null,

  setLiveKitData: (data) => set({ liveKitData: data }),
  setIsConnected: (isConnected) => set({ isConnected }),
  setIsConnecting: (isConnecting) => set({ isConnecting }),
  setError: (error) => set({ error }),
  setIsRecording: (isRecording) => set({ isRecording }),
  setAudioStream: (audioStream) => set({ audioStream }),
  setSessionActive: (sessionActive) => set({ sessionActive }),
  setWindowTitle: (windowTitle) => set({ windowTitle }),
  setLiveTranscriptSessionId: (sessionId) =>
    set({ liveTranscriptSessionId: sessionId }),
  applyLiveTranscriptLine: (line) =>
    set((state) => {
      if (
        isStaleTranscriptSession(
          state.liveTranscriptSessionId,
          line.sessionId,
        )
      ) {
        return state;
      }

      return {
        liveTranscriptSessionId:
          state.liveTranscriptSessionId || line.sessionId || null,
        liveTranscript: upsertLiveTranscriptLine(state.liveTranscript, line),
      };
    }),
  clearLiveTranscript: () =>
    set({ liveTranscript: [], liveTranscriptSessionId: null }),
  reset: () =>
    set({
      liveKitData: null,
      isConnected: false,
      isConnecting: false,
      error: null,
      isRecording: false,
      audioStream: null,
      sessionActive: false,
      windowTitle: null,
      liveTranscript: [],
      liveTranscriptSessionId: null,
    }),
}));
